/**
 * The consumer hook. Overlays pending operations onto a document, and folds confirmed ones into the
 * caches it names.
 *
 * Structural operations are opt-in per connection (`connections`), because an insert position and
 * its count deltas are meaningful only in a document that actually renders that connection. Entity
 * `setFields` operations are not: an issue's fields can be edited from a side panel, a row, a
 * picker, or another page, so this hook matches them by identity wherever they appear rather than
 * making the document enumerate every id it might hold — an enumeration that silently misses edits
 * as soon as it falls behind the selection set.
 */
import {type QueryClient, useQueryClient} from '@github-ui/react-query'
import {useEffect, useEffectEvent, useMemo, useRef} from 'react'

import {
  applyDocumentOperation,
  applyOperations,
  type ConnectionOperation,
  connectionPathKey,
  createRecordDocumentAdapter,
  type DocumentAdapter,
  type DocumentAdapterOptions,
  isEntityOperation,
  operationNeedsIncorporation,
  type PendingConnectionOperation,
  removeIncorporatedOperation,
} from './connection-operations'
import {settlementHandlers} from './settlement-registry'
import {
  type ConnectionMutationTarget,
  CONNECTION_MUTATION_KEY_ROOT,
  type ConnectionMutationVariables,
  connectionTargetKey,
  usePendingOperationEntries,
} from './pending-operations'
import {settleConfirmedOperation, type SettleTarget} from './settle-confirmed-operation'

export type UseOptimisticDataOptions<T> = DocumentAdapterOptions<NonNullable<T>> & {
  /** Connections this document renders, to also receive their structural operations. */
  connections?: ConnectionMutationTarget[]
  /**
   * The query keys this document renders from. Supply them to also receive **confirmed** values:
   * when a mutation succeeds, the server's fields are folded into these keys before the overlay
   * drops, so the value never flickers back through its pre-mutation state. Set `revalidate` on a
   * target to also refetch it, reconciling server-derived fields the operation doesn't carry
   * (timestamps, counts, pagination).
   *
   * Omit for a read-only view of someone else's data — the overlay still applies, but nothing is
   * written back. Explicit view adapters are not inherited by cache targets.
   */
  targets?: SettleTarget[]
  enabled?: boolean
}

export function useOptimisticData<T>(
  base: T,
  {
    documentAdapter,
    identityPath,
    mapFields,
    mapEntityField,
    applyEntityFieldItem,
    applyEntityFieldValuesDelta,
    updateEntityFieldItems,
    connections,
    targets,
    enabled = true,
  }: UseOptimisticDataOptions<NoInfer<T>> = {},
): T {
  const pendingEntries = usePendingOperationEntries()

  // Arrays and objects are new each render; compare by stable, collision-free keys so a caller
  // passing an inline `connections` array doesn't invalidate the overlay memo and re-walk the whole
  // document on every parent render. `mapFields` can't be normalised this way — callers must keep
  // it stable (a module-level function or a `useCallback`).
  const identityKey = identityPath ? connectionPathKey(identityPath) : undefined
  const compatibilityAdapter = useMemo(
    () =>
      createRecordDocumentAdapter({
        identityPath: identityKey ? (JSON.parse(identityKey) as string[]) : undefined,
        mapFields,
        mapEntityField,
        applyEntityFieldItem,
        applyEntityFieldValuesDelta,
        updateEntityFieldItems,
      }),
    [identityKey, mapFields, mapEntityField, applyEntityFieldItem, applyEntityFieldValuesDelta, updateEntityFieldItems],
  )
  const adapter: DocumentAdapter<NonNullable<T>> = documentAdapter ?? compatibilityAdapter
  const selectedTargets = targets?.map(target => ({
    ...target,
    documentAdapter: target.documentAdapter ?? compatibilityAdapter,
  }))
  useSettlementRegistration({targets: selectedTargets, connections, enabled})

  const connectionsKey = JSON.stringify(
    (connections ?? []).map(target => connectionTargetKey(target.ownerId, target.connectionPath)),
  )
  const connectionKeys = useMemo(() => new Set(JSON.parse(connectionsKey) as string[]), [connectionsKey])

  return useMemo(() => {
    // Before scanning: an idle page must not walk its document on every mutation-cache
    // notification, and most notifications belong to some other part of the page.
    if (!enabled || base == null || pendingEntries.length === 0) return base

    const operations = pendingEntries.flatMap<PendingConnectionOperation>(entry => {
      if (entry == null) return []
      return isEntityOperation(entry.operation) ||
        connectionKeys.has(connectionTargetKey(entry.ownerId, entry.connectionPath))
        ? [entry]
        : []
    })

    return operations.length === 0 ? base : applyOperations(base, operations, {documentAdapter: adapter})
    // `pendingEntries` is referentially stable across renders (via `replaceEqualDeep`).
  }, [enabled, base, pendingEntries, adapter, connectionKeys])
}

/**
 * Registers this document to receive confirmed values, so a successful mutation folds the server's
 * fields into `targets` *before* its overlay drops. Without it the overlay would drop onto a base
 * that never received the write, and the value would visibly revert until the next refetch.
 *
 * Internal to `useOptimisticData` on purpose: overlay and settlement share the same structural
 * subscriptions. Representation is selected at the boundary, independently for each document.
 */
/** The registration a document is leaving, captured so cleanup describes it and not its successor. */
type SettlementRegistrationConfig = {
  targets: Array<SettleTarget & {documentAdapter: DocumentAdapter}>
  connections?: ConnectionMutationTarget[]
}

function useSettlementRegistration({
  targets,
  connections,
  enabled,
}: {
  targets?: SettlementRegistrationConfig['targets']
  connections?: ConnectionMutationTarget[]
  enabled: boolean
}): void {
  const queryClient = useQueryClient()
  const registeredConfigRef = useRef<SettlementRegistrationConfig | undefined>(undefined)

  const handler = useEffectEvent(
    async (
      operation: ConnectionOperation,
      ownerId: string,
      connectionPath: string[],
      operationId?: string,
      options?: {forceRevalidate?: boolean},
    ) => {
      if (!targets || targets.length === 0) return

      if (isEntityOperation(operation)) {
        // Entity edits are judged by containment, which `settleConfirmedOperation` checks per
        // target — a document holding the entity settles, one that doesn't is skipped.
        const incorporateOperation = operationId
          ? pendingRelativeEntityOperationIds(queryClient).has(operationId)
          : false
        await settleConfirmedOperation({
          queryClient,
          targets,
          // A `setFields` names its own target, so the owner is the entity itself.
          ownerId: operation.id,
          operation,
          operationId,
          incorporateOperation,
          forceRevalidate: options?.forceRevalidate,
        })
        return
      }

      // Structural operations mean something only in a document that renders that exact
      // connection, which is the same list the overlay opts into.
      const rendersIt = (connections ?? []).some(
        target =>
          connectionTargetKey(target.ownerId, target.connectionPath) === connectionTargetKey(ownerId, connectionPath),
      )
      if (!rendersIt) return
      await settleConfirmedOperation({queryClient, targets, ownerId, operation, operationId})
    },
  )

  /**
   * A mutation still in flight when this document unregisters settles into a registry that no
   * longer holds it, so the fold that would have reconciled `base` never happens. Refresh instead
   * of leaving a still-optimistic base behind.
   *
   * Containment-filtered rather than blanket-invalidated: the entity channel is global, so every
   * field edit anywhere in the app is "outstanding" here, and unmounting during one would otherwise
   * cost this document a refetch it has no stake in.
   */
  const abandonOutstanding = useEffectEvent((outgoing: SettlementRegistrationConfig) => {
    const pending = queryClient
      .getMutationCache()
      .findAll({status: 'pending', mutationKey: [CONNECTION_MUTATION_KEY_ROOT]})
      .flatMap(mutation => {
        const variables = mutation.state.variables as ConnectionMutationVariables | undefined
        const operation = variables?.operation
        const key = mutation.options.mutationKey
        if (!operation || !Array.isArray(key)) return []
        return [
          {
            operation,
            operationId: variables?.operationId ?? variables?.mutationId,
            ownerId: String(key[1]),
            connectionPath: Array.isArray(key[2]) ? (key[2] as string[]) : [],
          },
        ]
      })
    if (pending.length === 0) return

    // Structural operations are claimed by the connections this document renders; entity ones by
    // whether it holds the entity. Both read the *outgoing* configuration for the same reason the
    // targets do -- see the capture in the effect below.
    const rendersConnection = (ownerId: string, connectionPath: string[]) =>
      (outgoing.connections ?? []).some(
        target =>
          connectionTargetKey(target.ownerId, target.connectionPath) === connectionTargetKey(ownerId, connectionPath),
      )

    for (const target of outgoing.targets) {
      const base = queryClient.getQueryData(target.queryKey)
      if (base === undefined) continue
      let mine: boolean
      try {
        mine = pending.some(
          entry =>
            (isEntityOperation(entry.operation) || rendersConnection(entry.ownerId, entry.connectionPath)) &&
            applyDocumentOperation(base, entry, target.documentAdapter) !== undefined,
        )
      } catch (error) {
        reportCleanupError(error)
        mine = true
      }
      if (mine) void queryClient.invalidateQueries({queryKey: target.queryKey})
    }
  })

  const pruneIncorporatedOperation = useEffectEvent((operation: ConnectionOperation, operationId: string) => {
    if (!targets || !operationNeedsIncorporation(operation)) return
    for (const target of targets) {
      const base = queryClient.getQueryData(target.queryKey)
      if (base === undefined) continue
      let next: typeof base
      try {
        next = removeIncorporatedOperation(base, undefined, operationId)
      } catch (error) {
        // Runs inside TanStack's mutation-cache notification, so a throw here would turn the
        // mutation that just succeeded into a failure.
        reportCleanupError(error)
        continue
      }
      if (next === base) continue
      const state = queryClient.getQueryState(target.queryKey)
      queryClient.setQueryData(target.queryKey, next, state ? {updatedAt: state.dataUpdatedAt} : undefined)
    }
  })

  const targetsKey = JSON.stringify((targets ?? []).map(target => target.queryKey))

  // Passive cleanup runs before the next passive setup, so it sees the outgoing commit.
  // Refresh adapters without making equivalent inline target arrays re-register.
  useEffect(() => {
    registeredConfigRef.current = targets ? {targets, connections} : undefined
  })

  useEffect(() => {
    if (!enabled || !targets || targets.length === 0) return

    const mutationCache = queryClient.getMutationCache()
    const unsubscribe = mutationCache.subscribe(event => {
      if (event.type !== 'updated' || event.mutation.state.status === 'pending') return
      const variables = event.mutation.state.variables as ConnectionMutationVariables | undefined
      const key = event.mutation.options.mutationKey
      const operationId = variables?.operationId ?? variables?.mutationId
      if (!variables || !operationId || !Array.isArray(key)) return

      const duplicatePending = mutationCache
        .findAll({
          mutationKey: [CONNECTION_MUTATION_KEY_ROOT],
          status: 'pending',
        })
        .some(mutation => {
          const pendingVariables = mutation.state.variables as ConnectionMutationVariables | undefined
          return (pendingVariables?.operationId ?? pendingVariables?.mutationId) === operationId
        })
      if (!duplicatePending) pruneIncorporatedOperation(variables.operation, operationId)
    })

    settlementHandlers.add(handler)
    return () => {
      unsubscribe()
      settlementHandlers.delete(handler)
      const registered = registeredConfigRef.current
      if (registered) abandonOutstanding(registered)
    }
    // Effect Events are stable across renders; `targetsKey`
    // stands in for the array so an inline `targets` doesn't re-register every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, targetsKey])
}

function reportCleanupError(error: unknown): void {
  // Report outside React's cleanup so every target can still be invalidated.
  setTimeout(() => {
    throw error
  }, 0)
}

function pendingRelativeEntityOperationIds(queryClient: QueryClient): Set<string> {
  return new Set(
    queryClient
      .getMutationCache()
      .findAll({mutationKey: [CONNECTION_MUTATION_KEY_ROOT], status: 'pending'})
      .flatMap(mutation => {
        const variables = mutation.state.variables as ConnectionMutationVariables | undefined
        const operationId = variables?.operationId ?? variables?.mutationId
        return variables &&
          (variables.operation.type === 'deltaEntityField' || variables.operation.type === 'deltaEntityFieldValues') &&
          operationId
          ? [operationId]
          : []
      }),
  )
}
